import { useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SERVICES, getService } from "@/data/services";
import { ArrowRight, ArrowLeft, CheckCircle2, Phone, CircleUserRound } from "lucide-react";

const MOMENTS = {
  identite:              ["Préparation d'un projet", "Création d'entreprise"],
  strategie_commerciale: ["Préparation d'un projet", "Développement d'entreprise"],
  strategie_marketing:   ["Préparation d'un projet", "Développement d'entreprise"],
  outils:                ["Création d'entreprise", "Développement d'entreprise"],
  acquisition:           ["Création d'entreprise", "Développement d'entreprise"],
  deploiement:           ["Développement d'entreprise"],
  consulting:            ["Préparation d'un projet", "Tout au long du parcours"],
};

export default function ServicePage() {
  const { serviceId } = useParams();
  const nav = useNavigate();
  const service = useMemo(() => getService(serviceId), [serviceId]);

  useEffect(() => {
    if (!service) {
      const t = setTimeout(() => nav("/", { replace: true }), 200);
      return () => clearTimeout(t);
    }
  }, [service, nav]);

  if (!service) {
    return (
      <>
        <Header />
        <main className="pt-40 pb-20 min-h-screen bg-[#FAFAFA] text-center" data-testid="service-not-found">
          <p className="text-[#64748B]">Service introuvable, redirection…</p>
        </main>
        <Footer />
      </>
    );
  }

  const Icon = service.icon;
  const moments = MOMENTS[service.id] || ["Tout au long du parcours"];

  // Related services (other ones)
  const related = SERVICES.filter((s) => s.id !== service.id).slice(0, 3);

  return (
    <>
      <Header />
      <main className="pt-32 sm:pt-40 pb-20 min-h-screen bg-[#FAFAFA]" data-testid={`service-page-${service.id}`}>
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-[#0A1128] via-[#14213d] to-[#0A1128] text-white overflow-hidden">
          <div className="absolute -top-32 -right-20 w-[420px] h-[420px] rounded-full opacity-25 pointer-events-none" style={{ background: service.color, filter: "blur(140px)" }} />
          <div className="absolute -bottom-20 -left-20 w-[300px] h-[300px] rounded-full opacity-15 pointer-events-none" style={{ background: "#06B6D4", filter: "blur(100px)" }} />

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <Link to="/" className="inline-flex items-center gap-1.5 text-[12px] text-white/65 hover:text-[#67E8F9] mb-4 transition-colors">
              <ArrowLeft size={12} /> Retour
            </Link>
            <div className="flex items-start gap-5 flex-wrap">
              <div className="shrink-0 inline-flex items-center justify-center w-16 h-16 rounded-2xl border-2 border-white/20" style={{ background: `${service.color}22` }}>
                <Icon size={28} style={{ color: service.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold tracking-[0.3em] uppercase" style={{ color: service.color }}>Service · {service.n}</div>
                <h1 className="display text-3xl sm:text-5xl font-extrabold leading-[1.05] mt-2">{service.title}</h1>
                <p className="text-base sm:text-lg text-white/80 mt-4 max-w-3xl leading-relaxed">{service.desc}</p>
              </div>
            </div>

            {/* Moments badges */}
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="text-[10px] font-bold tracking-widest uppercase text-white/55 px-2 pt-1">Intervient pendant :</span>
              {moments.map((m) => (
                <span key={m} className="inline-flex items-center gap-1.5 text-[12px] font-bold px-3 py-1.5 rounded-full bg-white/8 border border-white/15 backdrop-blur">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: service.color }} />{m}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Items */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="display text-2xl sm:text-3xl font-extrabold text-[#0A1128]">Ce que vous obtenez concrètement</h2>
              <ul className="mt-6 space-y-3">
                {service.items.map((it, i) => (
                  <li key={i} className="bg-white rounded-2xl border border-black/8 p-4 flex items-start gap-3" data-testid={`service-item-${i}`}>
                    <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: service.color }} />
                    <span className="text-[#0A1128] font-medium">{it}</span>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="lg:col-span-1">
              <div className="bg-[#0A1128] text-white rounded-3xl p-6 relative overflow-hidden sticky top-44">
                <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full opacity-30" style={{ background: service.color, filter: "blur(70px)" }} />
                <div className="relative">
                  <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#67E8F9]">Premier pas</div>
                  <h3 className="display text-xl font-extrabold mt-2">On démarre comment ?</h3>
                  <p className="text-sm text-white/70 mt-3 leading-relaxed">Un RDV découverte gratuit pour cadrer votre besoin, votre timing et votre budget. Sans engagement.</p>
                  <Link to="/rendez-vous" data-testid="service-cta-rdv" className="mt-5 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] font-extrabold text-sm transition-colors">
                    <Phone size={14} />Prendre RDV
                    <ArrowRight size={14} />
                  </Link>
                  <Link to="/portail/connexion" data-testid="service-cta-portal" className="mt-2 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full border border-white/20 hover:border-white/40 text-white font-bold text-sm transition-colors">
                    <CircleUserRound size={14} />Créer mon Espace
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* Related */}
        <section className="bg-white py-12 sm:py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#06B6D4]">À combiner avec</div>
            <h2 className="display text-2xl sm:text-3xl font-extrabold text-[#0A1128] mt-1">Autres services qui s'articulent bien</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((s) => (
                <Link key={s.id} to={`/services/${s.id}`} data-testid={`service-related-${s.id}`} className="group bg-[#FAFAFA] rounded-2xl border border-black/8 p-5 hover:border-[#06B6D4] transition-all hover:shadow-md">
                  <s.icon size={22} style={{ color: s.color }} />
                  <div className="display font-extrabold text-[#0A1128] mt-3">{s.short}</div>
                  <p className="text-xs text-[#64748B] mt-1 line-clamp-2">{s.desc}</p>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#06B6D4] group-hover:gap-2 transition-all">Voir le service <ArrowRight size={11} /></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
